import React, { memo, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { dates, formats, validators } from 'investira.sdk';
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  ListItemText,
  Chip,
  Alert
} from 'investira.react.components';
import { VALIDATIONS } from '../../../const';
import { useRelatorioContext } from '../../../contexts/RelatorioContext';
import services from '../../../services';
import withResponseHandling from '../../../hoc/withResponseHandling';
import withSaveParams from '../../../hoc/withSaveParams';
import withCenarios from '../../../hoc/withCenarios';
import { ObjectBuilder } from '../../../components/organisms';
import { INDICADOR_TIPO } from '@investirapri/financa.js/lib/enums';

// 1) carteira
// 2) data
// 3) dias
// 4) simulação
// 3) aberto/fechado

const VALIDATION_SCHEMA = Yup.object().shape({
  carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
  data: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
  //aberta: Yup.bool().required(VALIDATIONS.REQUIRED),
  dias: Yup.number().required(VALIDATIONS.REQUIRED),
  simulacao: Yup.object().nullable()
});

const StressTest = memo(props => {
  const { state, actions } = useRelatorioContext();

  const storedIndicadores = useSelector(store => store.indicadores.data);

  const cenariosFavoritos = props.cenarios.filter(xItem => xItem.favorito === true);

  const navigate = useNavigate();
  const location = useLocation();

  const storedParams = props.storedParams('stresstest', props.lastOutput);

  const [datas, setDatas] = useState([]);
  const [posicaoAberta, setPosicaoAberta] = useState(false);
  const [benchmarks, setBenchmarks] = useState(false);

  const xInitialValues = {
    carteira_id: '',
    data: storedParams.data || null,
    //aberta: storedParams.aberta || false,
    dias: storedParams.dias || 251,
    simulacao: storedParams.simulacao || null,
    consolidacao_carteira_tipo: 'I'
  };

  function groupIndicadores(pValues) {
    // Reagrupar por indicador_tipo_id
    // Ordenação dos grupos: C, J, I, M
    const ordemTipoId = ['C', 'J', 'I', 'M'];
    const xIndicadoresGroupedTemp = pValues.reduce((acc, indicador) => {
      const tipoId = indicador.indicador_tipo_id;
      if (!acc[tipoId]) {
        acc[tipoId] = [];
      }
      acc[tipoId].push(indicador);
      return acc;
    }, {});
    // Reordena os grupos conforme a ordem especificada
    const xIndicadoresGrouped = {};
    ordemTipoId.forEach(tipoId => {
      if (xIndicadoresGroupedTemp[tipoId]) {
        xIndicadoresGrouped[tipoId] = xIndicadoresGroupedTemp[tipoId];
      }
    });

    return xIndicadoresGrouped;
  }

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.stresstest(
        {
          ...pValues,
          consolidacao_carteira_tipo: 'I'
        },
        rRes => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams('stresstest', pValues);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    }
  });

  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const handleSimulacaoChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const xIndicadores = useMemo(() => {
    const xIndicadoresFlatted = Object.values(storedIndicadores || {}).flatMap(pLevel =>
      Array.isArray(pLevel) ? pLevel : Object.values(pLevel)
    );

    const xIndicadoresFiltered = xIndicadoresFlatted.filter(xIndicador => {
      return ['DI', 'USD', 'IPCA', 'IGPM', 'INPC', 'IBOV', 'IMAB', 'IFIX'].includes(
        xIndicador.indicador_id
      );
    });

    // Reagrupar por indicador_tipo_id
    // Ordenação dos grupos: C, J, I, M
    const xIndicadoresGrouped = groupIndicadores(xIndicadoresFiltered);

    return xIndicadoresGrouped;
  }, [storedIndicadores]);

  useEffect(() => {
    const xValues = storedParams;

    console.log('storedParams', storedParams);

    if (!validators.isEmpty(xValues)) {
      const { carteira_id, data, dias, simulacao, aberta } = xValues;
      formik.setValues({
        ...xInitialValues,
        carteira_id: carteira_id || '',
        data: data || null,
        dias: dias || 251,
        simulacao: simulacao || null,
        aberta: aberta || false,
        consolidacao_carteira_tipo: 'I'
      });
    }

    setBenchmarks(xIndicadores);
    services.benchmarks.list({}, rRes => {
      if (!validators.isEmpty(rRes.data)) {
        const groupedBenchmarks = groupIndicadores(rRes.data);

        setBenchmarks(groupedBenchmarks);
      }
    });
  }, []);

  const hasBenchmarks =
    validators.isEmpty(cenariosFavoritos) && validators.isEmpty(formik.values.simulacao);

  useEffect(() => {
    if (state.isDialogOpen && formik.values.carteira_id !== '') {
      setDatas([]);

      formik.values.carteira_id &&
        services.posicoes.datas(
          {
            sort: 'data DESC',
            carteira_id: formik.values.carteira_id
          },
          rRes => {
            const xDates = rRes.data.map(xPosicao => {
              return {
                value: formats.formatDateCustom(dates.toDate(xPosicao.data), 'YYYY-MM-DD'),
                label: formats.formatDateCustom(dates.toDate(xPosicao.data), 'DD/MM/YYYY'),
                aberta: xPosicao.aberta
              };
            });

            setDatas(xDates);

            const xValues = storedParams;

            if (!validators.isEmpty(xValues) && formik.values.carteira_id === xValues.carteira_id) {
              const { data, aberta } = xValues;
              formik.setFieldValue('data', data);

              const xAberta = xDates.filter(x => x.value === data)[0]?.aberta;

              setPosicaoAberta(xAberta);
              formik.setFieldValue('aberta', aberta); // Talvez não seja necessário
            } else {
              formik.setFieldValue('data', xDates[0].value);
              setPosicaoAberta(xDates[0].aberta);
            }
          },
          rErr => {
            props.responseErrorHandling(rErr);
          }
        );
    }
  }, [state.isDialogOpen, formik.values.carteira_id]);

  console.log('formik.values', formik.values);

  const xCarteiras = useMemo(
    () => props.carteiras.slice().sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras]
  );

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ marginBottom: 2, py: 1 }} spacing={2}>
        <Stack flexGrow={1}>
          <TextField
            id="carteira_id"
            name="carteira_id"
            label="Carteira"
            variant="outlined"
            value={formik.values.carteira_id}
            onChange={formik.handleChange}
            select
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="wallet" size={21} />
                </InputAdornment>
              )
            }}>
            {xCarteiras.map(xOption => (
              <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                {xOption.carteira}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {formik.values.carteira_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flex={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data-posicao"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={e => {
                      formik.handleChange(e);

                      const xAberta = datas.filter(x => x.value === e.target.value)[0].aberta;

                      setPosicaoAberta(xAberta);
                      if (!xAberta) {
                        formik.setFieldValue('aberta', false);
                      }
                    }}
                    sx={{
                      '& .MuiChip-root': {
                        display: 'none'
                      }
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      )
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}>
                    {datas.map((xOption, xIndex) => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={'Aberta'}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              <Stack flex={1}>
                <TextField
                  id="dias"
                  label="Amostragem"
                  name="dias"
                  type="number"
                  value={formik.values.dias}
                  variant="outlined"
                  onChange={e => {
                    // Permite apenas números inteiros positivos
                    const value = e.target.value;
                    if (value === '' || /^[0-9]+$/.test(value)) {
                      formik.setFieldValue('dias', value === '' ? '' : parseInt(value, 10));
                    }
                  }}
                  error={Boolean(formik.errors.dias)}
                  helperText={formik.errors.dias}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: 1,
                    step: 1,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    formNoValidate: 'formnovalidate',
                    maxLength: 9
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <ObjectBuilder
                label="Simulação"
                btnAddLabel="Adicionar Benchmark"
                chaveLabel="Benchmark"
                valorLabel="Variação"
                // mensagemVazio="Nenhum benchmark adicionado."
                initialValues={formik.values.simulacao}
                onChange={pValue => handleSimulacaoChange('simulacao', pValue)}
                groups={INDICADOR_TIPO}
                opcoesGrouped={benchmarks}
              />
            </Stack>
            {hasBenchmarks && (
              <Alert severity="warning">
                Defina algum cenário como favorito em configurações ou adicione algum benchmark à
                simulação acima.
              </Alert>
            )}
            {/* <Stack px={1}>
              <FormControlLabel
                control={
                  <Switch
                    id="aberta"
                    checked={formik.values.aberta}
                    onChange={(e, value) => {
                      handleSwitchChange("aberta", value);
                    }}
                    name="aberta"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">Posição aberta</Typography>
                }
                disabled={!posicaoAberta}
              />
            </Stack> */}
          </>
        )}

        <Stack alignItems="center" justifyContent="flex-end" direction="row" spacing={2}>
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={actions.handleNewConsultCancel}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              validators.isEmpty(datas) ||
              validators.isEmpty(formik.values.carteira_id) ||
              hasBenchmarks
            }>
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(withCenarios(withSaveParams(StressTest)));
